import { Injectable } from '@angular/core';
import { Chart, ChartConfiguration, ChartOptions, ChartType, DefaultDataPoint, Plugin } from 'chart.js';
import { Context } from 'chartjs-plugin-datalabels/types/context';
import { first } from 'lodash';

import { ChartTypes } from '@dashboards/models/chart-types.enum';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { ChartConfig, ChartDataItem } from '@shared/interfaces/chart-data-item';

@Injectable()
export class ChartService {
  labels: string[] = [];
  data: number[] = [];
  backgroundColor: string[] = [];
  labelColor: string[] = [];
  chartDataList: ChartDataItem[] = [];

  getChartConfig(
    chartType: ChartTypes,
    chartDataList: ChartDataItem[],
    chartConfig: ChartConfig
  ): ChartConfiguration<ChartType, DefaultDataPoint<ChartType>, string> {
    const baseDataset: any = {
      label: '',
      data: this.data,
      datalabels: { color: this.labelColor },
    };

    if (chartType === ChartTypes.LINE) {
      Object.assign(baseDataset, {
        borderColor: chartConfig.borderColor ?? EColorPalette.cBlue,
        backgroundColor: chartConfig.pointColor ?? EColorPalette.cBlue,
        fill: false,
        tension: 0,
        pointRadius: 4,
        pointHoverRadius: 4,
      });
    } else {
      Object.assign(baseDataset, {
        maxBarThickness: chartConfig.maxBarThickness,
        offset: 0,
        spacing: chartConfig.spacing ?? 0,
        borderWidth: chartConfig.borderWidth ?? 0,
        borderColor: chartConfig.borderColor ?? 'transparent',
        backgroundColor: this.backgroundColor,
        hoverOffset: 10,
        hoverBorderWidth: 0,
        hoverBorderColor: 'transparent',
        borderRadius: chartConfig?.borderRadius || 0,
      });
    }

    const config: ChartConfiguration<ChartType, DefaultDataPoint<ChartType>, string> = {
      type: chartType,
      data: {
        labels: this.labels,
        datasets: [baseDataset],
      },
      options: this.getChartOptions(chartType, chartConfig, chartDataList),
      plugins: [],
    };

    if (chartType === ChartTypes.DOUGHNUT && chartConfig.showTextTooltip) {
      config.plugins.push(this.getDoughnutTextPlugin());
    }

    return config;
  }

  getChartOptions(
    chartType: ChartTypes,
    chartConfig: ChartConfig,
    chartDataList?: ChartDataItem[]
  ): ChartOptions<ChartTypes> {
    let options: ChartOptions<ChartTypes> = {
      responsive: true,

      layout: {
        padding: 10,
      },

      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: chartConfig.showTooltip,
          callbacks: chartConfig.useTooltipTemplate
            ? {
                title: context => {
                  const index = context[0]?.dataIndex;
                  const label = chartDataList[index]?.label ?? '';
                  return this.splitByWordsOrCamelCase(label, 2, 12);
                },
                label: context => {
                  const index = context.dataIndex;
                  return chartDataList[index]?.value?.toString() ?? '';
                },
                labelColor: context => {
                  return {
                    borderColor: 'transparent',
                    backgroundColor: (context.dataset.backgroundColor as string[])[context.dataIndex],
                    borderWidth: 0,
                  };
                },
              }
            : {},
        },
        datalabels: {
          color: EColorPalette.cWhite,
          font: {
            size: 10,
          },
          display: chartConfig.showDataLabel,
          formatter: this.getDataLabelFormatter(chartConfig),
        },
      },
    };

    if (chartType === ChartTypes.DOUGHNUT) {
      options = {
        ...options,
        cutout: `${chartConfig.cutoutLevel}%`,
        rotation: chartConfig.rotation,
        circumference: chartConfig.circumference,
      };
    }

    if (chartType === ChartTypes.BAR) {
      options = {
        ...options,
        scales: {
          xAxis: {
            display: true,
            grid: {
              display: false,
            },
          },
          yAxis: {
            display: false,
            grid: {
              display: false,
            },
          },
        },
      };
    }

    if (chartType === ChartTypes.LINE) {
      options = {
        ...options,
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
          },
          y: {
            display: true,
            grid: {
              display: true,
            },
          },
        },
        elements: {
          line: {
            tension: 0.3,
            borderWidth: 2,
          },
          point: {
            radius: 4,
            hoverRadius: 6,
            backgroundColor: EColorPalette.cBlue,
          },
        },
      };
    }

    return options;
  }

  getDoughnutTextPlugin(): Plugin<ChartTypes> {
    const dataList = this.chartDataList;

    return {
      id: 'dougnut inner text',
      beforeDraw(chart: Chart, args, options) {
        const activeElement = first(chart.getActiveElements());
        if (!activeElement) {
          return;
        }

        const { label, additionalValue, additionalPercentage } = dataList[activeElement?.index];
        const {
          ctx,
          chartArea: { top, right, bottom, left, width, height },
        } = chart;
        ctx.save();
        ctx.fillStyle = EColorPalette.cGray;
        ctx.font = '10px lato';
        ctx.textAlign = 'center';
        ctx.fillText(`${(activeElement.element as any).$context.raw} units`, width / 2 + 10, top - 10 + height / 2);
        ctx.fillText(label, width / 2 + 10, top + height / 2);
        ctx.fillStyle = EColorPalette.cGray5;
        ctx.textAlign = 'center';
        ctx.fillText(`$${additionalValue.toFixed(2)}`, width / 2 + 10, top + 10 + height / 2);
        const textPercentage = `${additionalPercentage.toFixed(2)}%`;
        ctx.fillText(textPercentage, width / 2 + 10, top + 20 + height / 2);
      },
    };
  }

  getChartData(chartDataList: ChartDataItem[]) {
    this.labels = [];
    this.data = [];
    this.backgroundColor = [];
    this.labelColor = [];
    this.chartDataList = chartDataList;

    chartDataList.forEach((chartDataItem: ChartDataItem) => {
      this.labels.push(chartDataItem.label);
      this.data.push(chartDataItem.value);
      this.backgroundColor.push(chartDataItem.backgroundColor);
      this.labelColor.push(chartDataItem.labelColor);
    });
  }

  getDataSum(): number {
    return this.data.reduce((acc, value) => acc + value);
  }

  getDataLabelFormatter(config: ChartConfig) {
    if (config.dataLabelAbsolute) {
      return value => value;
    } else {
      const getContextSum = (context: Context): number => {
        return (context.dataset.data.reduce((prev, curr) => (prev as number) + (curr as number)) as number) || 0;
      };
      return (value, context) => {
        return `${value ? Math.round((value / getContextSum(context)) * 100) : ''}${value ? '%' : ''}`;
      };
    }
  }

  private splitByWordsOrCamelCase(str: string, maxWordsPerLine = 3, maxLineLength = 15): string[] {
    const words = str.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g) || [];
    const result: string[] = [];

    let line = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = line ? `${line} ${word}` : word;

      if (testLine.length > maxLineLength || line.split(' ').length >= maxWordsPerLine) {
        result.push(line);
        line = word;
      } else {
        line = testLine;
      }
    }

    if (line) result.push(line);

    return result;
  }
}
